import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Form,
  LoadingSpinner,
  ModeToggle,
  PasswordFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { LawWorksLogo } from "@/components/ui/LawWorksLogo/LawWorksLogo";
import {
  useConfirmResetPassword,
  useConfirmSignIn,
} from "@/lib/data/react-query/auth/auth";
import { PasswordSchema } from "@/lib/data/schemas";
import type { APIError } from "@/lib/types";
import { redirectIfAuthenticated } from "@/lib/utils/auth";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

export const Route = createFileRoute("/reset-password")({
  validateSearch: z.object({
    redirect: z.string().optional().catch(""),
  }),
  component: RouteComponent,
  beforeLoad: ({ context, location }) => {
    redirectIfAuthenticated({ session: context.session, location });
  },
});

export const resetPasswordFormSchema = z
  .object({
    email: z.string().email().max(50),
    newPassword: PasswordSchema,
    confirmNewPassword: PasswordSchema,
    confirmationCode: z
      .string()
      .min(6, { message: "Confirmation code is 6 characters" }),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Passwords don't match",
    path: ["confirmNewPassword"],
  });

function RouteComponent() {
  const confirmSignIn = useConfirmSignIn();
  const confirmResetPassword = useConfirmResetPassword();
  const navigate = useNavigate({ from: "/reset-password" });

  const form = useForm<z.infer<typeof resetPasswordFormSchema>>({
    resolver: zodResolver(resetPasswordFormSchema),
    values: {
      email: "",
      newPassword: "",
      confirmNewPassword: "",
      confirmationCode: "",
    },
    mode: "onSubmit",
  });

  const onSubmit = async () => {
    await confirmResetPassword
      .mutateAsync({
        email: form.getValues("email"),
        newPassword: form.getValues("newPassword"),
        verificationCode: form.getValues("confirmationCode"),
      })
      .then(() => {
        toast.success("Password changed successfully");
        void navigate({ to: "/login" });
      })
      .catch((err: unknown) => {
        toastAPIErrorMessage(
          err as AxiosError<APIError>,
          "Unable to reset password. Please try again.",
        );
      });
  };

  return (
    <div className="flex h-[99vh] w-[99vw] grow content-center justify-center overflow-hidden">
      <div className="sm:px-auto my-auto w-full max-w-[500px] px-4">
        <Card className="p-6">
          <CardHeader>
            <LawWorksLogo className="justify-self-center" />
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="mt-8 flex flex-col gap-y-4"
              >
                <TextFormField
                  register={form.register("email")}
                  label="Email"
                ></TextFormField>
                <TextFormField
                  register={form.register("confirmationCode")}
                  label="Confirmation code"
                ></TextFormField>
                <PasswordFormField
                  register={form.register("newPassword")}
                  label="New password"
                />
                <PasswordFormField
                  register={form.register("confirmNewPassword")}
                  label="Confirm new password"
                />
                <Button
                  type="submit"
                  variant="foreground"
                  className="mt-4 w-full"
                >
                  {confirmSignIn.isPending ? (
                    <LoadingSpinner />
                  ) : (
                    "Change password"
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
          <div className="float-right pr-6">
            <ModeToggle />
          </div>
          <CardFooter className="flex flex-col items-start">
            <span className="mt-2 flex flex-wrap text-sm font-light">
              <Link to="/login">
                <Typography variant="link">Back to login</Typography>
              </Link>
            </span>
          </CardFooter>
        </Card>
      </div>
      <div></div>
    </div>
  );
}
